/**
 *  @class PredictiveSearch — live search drawer (Shopify predictive search API).
 */
class PredictiveSearch {
  constructor() {
    this.container = document.getElementById('Search-Drawer');
    if (!this.container) return;
    this.form = this.container.querySelector('form.searchform');
    this.buttons = document.querySelectorAll('.thb-quick-search');
    this.input = this.container.querySelector('input[type="search"]');
    this.results = this.container.querySelector('.side-panel-content--has-tabs');
    this.lastTrigger = null;
    this.highlightIndex = -1;

    if (!this.input || !this.results) return;
    this.setupEventListeners();
  }

  setupEventListeners() {
    if (this.form) this.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce(() => this.onChange(), 250));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));

    this.buttons.forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        this.lastTrigger = item;
        document.body.classList.toggle('open-cc');
        this.container.classList.toggle('active');
        if (this.container.classList.contains('active')) {
          setTimeout(() => this.input.focus({ preventScroll: true }), 100);
          dispatchCustomEvent('search:open');
        }
        return false;
      });
    });

    // Popular-term chips (default + no-results). Delegated so injected ones work.
    this.container.addEventListener('click', (e) => {
      const term = e.target.closest('.search-popular--term');
      if (!term) return;
      e.preventDefault();
      this.input.value = term.getAttribute('data-term') || term.textContent.trim();
      this.input.focus();
      this.onChange();
    });

    // Escape closes the drawer and returns focus to the search icon.
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.container.classList.contains('active')) {
        this.closeDrawer();
      }
    });
  }

  closeDrawer() {
    this.container.classList.remove('active');
    document.body.classList.remove('open-cc');
    if (this.lastTrigger) { try { this.lastTrigger.focus(); } catch (err) {} }
  }

  getQuery() { return this.input.value.trim(); }

  onChange() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) {
      this.results.classList.remove('active');
      this.results.innerHTML = '';            // restore the default content
      this.highlightIndex = -1;
      return;
    }
    this.results.classList.add('active');
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
  }

  getSearchResults(searchTerm) {
    this.results.classList.add('loading');     // dim previous results; no spinner
    fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,article,page&${encodeURIComponent('resources[limit]')}=6&section_id=predictive-search`)
      .then((response) => {
        this.results.classList.remove('loading');
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        if (section) this.renderSearchResults(section.innerHTML);
      })
      .catch(() => { this.results.classList.remove('loading'); });
  }

  renderSearchResults(markup) {
    this.results.innerHTML = markup;
    this.highlightIndex = -1;
  }

  resultItems() {
    return Array.prototype.slice.call(
      this.results.querySelectorAll(
        '.psr__products a.cart-product-link, .psr__article, .psr__page, .psr__view-all, .search-popular--term'
      )
    );
  }

  onKeydown(e) {
    if (!this.results.classList.contains('active')) return;
    const items = this.resultItems();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.highlightIndex = Math.min(this.highlightIndex + 1, items.length - 1);
      this.setHighlight(items);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.highlightIndex = Math.max(this.highlightIndex - 1, -1);
      this.setHighlight(items);
    } else if (e.key === 'Enter') {
      if (this.highlightIndex >= 0 && items[this.highlightIndex]) {
        e.preventDefault();
        items[this.highlightIndex].click();
      }
      // otherwise let the form submit to the full /search page
    }
  }

  setHighlight(items) {
    items.forEach((el, i) => el.classList.toggle('is-highlighted', i === this.highlightIndex));
    const active = items[this.highlightIndex];
    if (active) active.scrollIntoView({ block: 'nearest' });
  }
}

window.addEventListener('load', () => {
  if (typeof PredictiveSearch !== 'undefined') new PredictiveSearch();
});
